import { createReducer, on } from "@ngrx/store";
import { addUserProduct, deleteUserProducts, updateTemplatePublished, loadUserProducts, loadUserProductsFailed, loadUserProductsSiteInstalledInfo, loadUserProductsSiteInstalledInfoFailed, loadUserProductsSiteInstalledInfoSuccess, loadUserProductsSuccess, resetWorkspaceProducts, updateUserProductActivities, updateUserProductOutdatedInfo, updateUserProductSitesInstalledIn, updateUserProductStatus, updateUserProductVersions, updateUserProductsTenantOutdatedInfo, updateUserProductAudience, resetUserProductsSiteInstalledInfo, deleteTemplates, updateTemplate, upsertDraft, resetUserProducts } from "../actions/user-products.actions";
import { userProductsInitialState } from "../initial-states/user-products.state";
import { MultipleAppsExistsResponse, MultipleAppsExistsTenant, ProductActivity, ProductVersion, UserProduct } from "src/app/models/user-products.model";
import { SiteWithProductInstalled } from "src/app/models/user-resources.model";
import { ProductAudience } from "src/app/models/organization-catalog.model";

export const UserProductsReducer = createReducer(userProductsInitialState,

	// The reducer runs when a dispatch 'loadUserProducts' was triggered
	// The only thing we need to do here is to set the loading to true
	on(loadUserProducts,
		(state, { productType }) => {
			const newState = { ...state }
			newState[productType] = { ...state[productType], loading: true };
			return newState;
		}
	),

	on(resetUserProducts, 
		() => {
			return { ...userProductsInitialState };
		}
	),

	// The reducer runs when a dispatch 'loadUserProductsSuccess' was triggered
	// Here we need to set the loading to false, set the value of 'loadedOnce' and add the value of the catalog for the specific type
	on(loadUserProductsSuccess,
		(state, { productType, userProducts }) => {
			const newState = { ...state }
			newState[productType] = { ...state[productType], loading: false, loadedOnce: true, value: parseUserProducts(userProducts) };
			return newState;
		}
	),

	// The reducer runs when a dispatch 'loadUserProductsFailed' was triggered
	// Here we need to set the state to the initial one, set the value of 'loadedOnce' and add the value of the error action result into the store
	on(loadUserProductsFailed,
		(state, { productType, error }) => {
			const newState = { ...state }
			newState[productType] = { ...state[productType], loading: false, loadedOnce: false, error };
			return newState;
		}
	),

	// The reducer runs when a dispatch 'addUserProduct' was triggered
	// Here we need validate if exists the product type, if not, we need to create it
	// Then we need to add the new user product to the store
	//Replace product if already exists in the store, based on the product id
	on(addUserProduct,
		(state, { productType, userProduct }) => {
			const newState = { ...state }
			if(newState[productType].loadedOnce === true){
				const userProductIndex = newState[productType].value.findIndex(x => x.id === userProduct.id);
				if (userProductIndex > -1) {
					const currentUserProduct = newState[productType].value[userProductIndex];

					if (userProduct.isOutdatedInSiteCollection === undefined) {
						userProduct.isOutdatedInSiteCollection = currentUserProduct.isOutdatedInSiteCollection;
					}

					if (userProduct.isOutdatedInTenant === undefined) {
						userProduct.isOutdatedInTenant = currentUserProduct.isOutdatedInTenant;
					}

					if (userProduct.sitesWithProductInstalled === undefined) {
						userProduct.sitesWithProductInstalled = currentUserProduct.sitesWithProductInstalled;
					}

					newState[productType].value[userProductIndex] = parseUserProducts([userProduct])[0];
				} else {
					newState[productType].value.push(parseUserProducts([userProduct])[0]);
				}
			}
			return newState;
		}
	),

	// The reducer runs when a dispatch 'updateUserProductVersions' was triggered
	// Here we need to update the value of the user product versions
	on(updateUserProductVersions,
		(state, { userProductType, userProductVersions }) => {
			const newState = { ...state }
			newState[userProductType] = { ...state[userProductType], value: updateUserProductVersion(state[userProductType].value, userProductVersions) };
			return newState;
		}
	),

	// The reducer runs when a dispatch 'updateUserProductStatus' was triggered
	// Here we need to update the value of the user product status
	on(updateUserProductStatus,
		(state, { userProductType, userProductId, status }) => {
			const newState = { ...state }
			newState[userProductType] = { ...state[userProductType], value: updateProductStatus(state[userProductType].value, userProductId, status) };
			return newState;
		}
	),

	// The reducer runs when a dispatch 'deleteUserProduct' was triggered
	// Here we need to update the value of the user product status
	on(deleteUserProducts,
		(state, { userProductType, userProductIds }) => {
			const newState = { ...state };

			// Check if userProductType is valid
			if (newState[userProductType] !== undefined) {
				// Create a new array with the filtered items
				newState[userProductType].value = newState[userProductType].value.filter(item => !userProductIds.includes(item.id)
				);
			}

			return newState;
		}
	),

	// The reducer runs when a dispatch 'deleteTemplates' was triggered
	// Here we need to update the value of the user product status
	on(deleteTemplates,
		(state, { templateIds }) => {
			const newState = { ...state };
			if(newState["template"].loadedOnce){
				newState["template"].value = newState["template"].value.filter(item => !templateIds.includes(item.id))
			}

			if(newState["sharedtemplate"].loadedOnce){
				newState["sharedtemplate"].value = newState["sharedtemplate"].value.filter(item => !templateIds.includes(item.id))
			}
			return newState;
		}
	),

	// The reducer runs when a dispatch 'updateTemplate' was triggered
	// Here we need to update the value of the user product status
	on(updateTemplate,
		(state, { productType, template }) => {
			const newState = { ...state }
			const userProductIndex = newState[productType].value.findIndex(x => x.id === template.id);
			if (userProductIndex > -1) {
				newState[productType].value[userProductIndex] = parseUserProducts([template])[0];
			}

			return newState;
		}
	),

	// The reducer runs when a dispatch 'upsertDraft' was triggered
	// Here we need to update the value of the user product status
	on(upsertDraft,
		(state, { productType, template }) => {
			const newState = { ...state }

			if(newState[productType].loadedOnce){
				// If is my template is normal upsert, If is shared we check first if the generated templates exists (is shared) before upserting
				if(productType === "template" || newState[productType].value.find(x => x.id === template.name) !== undefined){
					const userProductIndex = newState[productType].value.findIndex(x => x.id === template.id);
					if (userProductIndex > -1) {
						newState[productType].value[userProductIndex] = parseUserProducts([template])[0];
					} else {
						newState[productType].value.push(parseUserProducts([template])[0]);
					}
				}
			}

			return newState;
		}
	),

	// The reducer runs when a dispatch 'updateUserProductOutdatedInfo' was triggered
	// Here we need to update the value of the user product outdatedInTenant and outdatedInSiteCollection attributes
	on(updateUserProductOutdatedInfo,
		(state, { productType, userProductId, outdatedInTenant, outdatedInSiteCollections }) => {
			const newState = { ...state }
			newState[productType] = { ...state[productType], value: updateUserProductOutdatedInformation(state[productType].value, userProductId, outdatedInTenant, outdatedInSiteCollections) };
			return newState;
		}
	),

	// The reducer runs when a dispatch 'updateUserProductActivities' was triggered
	// Here we need to update the value of the user product activities
	on(updateUserProductActivities,
		(state, { userProductType, userProductId, productActivities }) => {
			const newState = { ...state }
			newState[userProductType] = { ...state[userProductType], value: updateUserProductActivity(state[userProductType].value, userProductId, productActivities) };
			return newState;
		}
	),

	// The reducer runs when a dispatch 'updateUserProductsTenantOutdatedInfo' was triggered
	// Here we need to update the value of the multiple user products outdatedInTenant attribute
	on(updateUserProductsTenantOutdatedInfo,
		(state, { productType, outdatedInfo }) => {
			const newState = { ...state }
			newState[productType] = { ...state[productType], value: updateUserProductsTenantOutdatedInformation(state[productType].value, outdatedInfo) };
			return newState;
		}
	),

	// The reducer runs when a dispatch 'loadUserProductsSiteInstalledInfoSuccess' was triggered
	// Here we need to update the value of the multiple user products outdatedInSiteCollection and sitesWithProductOutdated attributes
	on(loadUserProductsSiteInstalledInfoSuccess,
		(state, { productType, installedInfo }) => {
			const newState = { ...state }
			newState[productType] = { ...state[productType], value: updateUserProductsSitesOutdatedInformation(state[productType].value, installedInfo), loadedInstalledInfoOnce: true, loadingInstalledInfo: false };
			return newState;
		}
	),

	// The reducer runs when a dispatch 'loadUserProductsSiteOutdatedInfoFailed' was triggered
	// Here we need to set the value of loadedOutdatedInfoOnce true and loadingOutdatedInfo false
	on(loadUserProductsSiteInstalledInfoFailed,
		(state, { productType }) => {
			const newState = { ...state }
			newState[productType] = { ...state[productType], loadedInstalledInfoOnce: true, loadingInstalledInfo: false };
			return newState;
		}
	),

	// The reducer runs when a dispatch 'updateUserProductSitesWithOutdatedInfo' was triggered
	// Here we need to update the value of the multiple user products sitesWithProductOutdated attributes
	on(updateUserProductSitesInstalledIn,
		(state, { productType, userProductId, sitesWithProduct }) => {
			const newState = { ...state }
			newState[productType] = { ...state[productType], value: updateUserProductSitesInstalled(state[productType].value, userProductId, sitesWithProduct) };
			return newState;
		}
	),

	// The reducer runs when a dispatch 'loadUserProductsSiteOutdatedInfo' was triggered
	// Here we need to update the value of the loadingOutdatedInfo to true
	on(loadUserProductsSiteInstalledInfo,
		(state, { productType }) => {
			const newState = { ...state }
			newState[productType] = { ...state[productType], loadingInstalledInfo: true };
			return newState;
		}
	),

	on(resetUserProductsSiteInstalledInfo,
		(state) => {
			const newState = { ...state }
			newState['theme'] = { ...state['theme'], value: resetProductsSiteInstalledInfo(state['theme'].value), loadedInstalledInfoOnce: false, loadingInstalledInfo: false };
			newState['webpart'] = { ...state['webpart'], value: resetProductsSiteInstalledInfo(state['webpart'].value), loadedInstalledInfoOnce: false, loadingInstalledInfo: false };
			newState['accessibility'] = { ...state['accessibility'], value: resetProductsSiteInstalledInfo(state['accessibility'].value) };
			newState['sharedtheme'] = { ...state['sharedtheme'], value: resetProductsSiteInstalledInfo(state['sharedtheme'].value), loadedInstalledInfoOnce: false, loadingInstalledInfo: false };

			return newState;
		}
	),

	// Reset the state to the initial one
	on(resetWorkspaceProducts,
		() => ({
			...userProductsInitialState
		})
	),

	on(updateTemplatePublished,
		(state) => ({
			...state
		})
	),
	on(updateUserProductAudience,
		(state, { audience }) => ({
			...state,
			template: { ...state.template, value: updateProductAudience(audience, state.template.value)},
			sharedtemplate: { ...state.sharedtemplate, value: updateProductAudience(audience, state.sharedtemplate.value)}
		})
	),


)

function parseUserProducts(userProducts: UserProduct[]): UserProduct[] {
	userProducts.map(userProduct => {

		if (userProduct.constraints.indexOf("program_insider") !== -1 && !userProduct.tags.includes("insiders")) {
			userProduct.tags.push("insiders");
		}

		const activeUntilYear = new Date(userProduct.activeUntil).getFullYear();
		const dateCreatedYear = new Date(userProduct.dateCreated).getFullYear();
		const dateLastModifyYear = new Date(userProduct.dateLastModify).getFullYear();

		if (activeUntilYear > 1601 && activeUntilYear < 9900) {
			userProduct.dateActiveUntil = new Date(userProduct.activeUntil);
		}

		if (dateCreatedYear > 1601 && dateCreatedYear < 9900) {
			userProduct.dateCreatedDate = new Date(userProduct.dateCreated);
		}

		if (dateLastModifyYear > 1601 && dateLastModifyYear < 9900) {
			userProduct.dateLastModifyDate = new Date(userProduct.dateLastModify);
		}

		return userProduct;
	});

	return userProducts;
}

function updateUserProductVersion(currentUserProducts: UserProduct[], productVersions: ProductVersion): UserProduct[] {
	const userProductToUpdateIndex = currentUserProducts.findIndex(x => x.id === productVersions.id);
	if (userProductToUpdateIndex !== -1) {
		currentUserProducts[userProductToUpdateIndex].versions = productVersions.versions
	}
	return currentUserProducts;
}

function updateProductStatus(currentUserProducts: UserProduct[], productId: string, status: string): UserProduct[] {
	const userProductToUpdateIndex = currentUserProducts.findIndex(x => x.id === productId);
	if (userProductToUpdateIndex !== -1) {
		currentUserProducts[userProductToUpdateIndex].status = status
	}
	return currentUserProducts;
}

function updateUserProductActivity(currentUserProducts: UserProduct[], productId: string, activities: ProductActivity[]): UserProduct[] {
	const userProductToUpdateIndex = currentUserProducts.findIndex(x => x.id === productId);
	if (userProductToUpdateIndex !== -1) {
		currentUserProducts[userProductToUpdateIndex].activities = activities
	}
	return currentUserProducts;
}

function updateUserProductOutdatedInformation(currentUserProducts: UserProduct[], productId: string, outdatedInTenant: boolean | undefined, outdatedInSiteCollections: boolean | undefined) {
	const userProductToUpdateIndex = currentUserProducts.findIndex(x => x.id === productId);
	if (userProductToUpdateIndex != -1) {
		if (outdatedInTenant != undefined) {
			currentUserProducts[userProductToUpdateIndex].isOutdatedInTenant = outdatedInTenant
		}
		if (outdatedInSiteCollections != undefined) {
			currentUserProducts[userProductToUpdateIndex].isOutdatedInSiteCollection = outdatedInSiteCollections;
		}
	}

	return currentUserProducts;
}

function updateUserProductSitesInstalled(currentUserProducts: UserProduct[], productId: string, siteCollections: SiteWithProductInstalled[]) {
	const userProductToUpdateIndex = currentUserProducts.findIndex(x => x.id === productId);
	if (userProductToUpdateIndex != -1) {
		if (siteCollections != undefined) {
			currentUserProducts[userProductToUpdateIndex].sitesWithProductInstalled = siteCollections;
		}
	}
	return currentUserProducts;
}

function updateUserProductsSitesOutdatedInformation(currentUserProducts: UserProduct[], installedInfo: MultipleAppsExistsResponse[]) {
	if (installedInfo === undefined || installedInfo.length === 0) {
		return currentUserProducts;
	}
	installedInfo.forEach(info => {
		updateUserProductSitesInstalled(currentUserProducts, info.productId, info.sites);
		updateUserProductOutdatedInformation(currentUserProducts, info.productId, undefined, info.isOutdated)
	});

	return currentUserProducts;
}

function updateUserProductsTenantOutdatedInformation(currentUserProducts: UserProduct[], outdatedInfo: MultipleAppsExistsTenant[]) {
	if (outdatedInfo === undefined || outdatedInfo.length === 0) {
		return currentUserProducts;
	}
	outdatedInfo.forEach(info => {
		updateUserProductOutdatedInformation(currentUserProducts, info.productId, info.exists, undefined)
	});

	return currentUserProducts;
}

function updateProductAudience(audience: ProductAudience[], currentUserProducts: UserProduct[]){
	if (audience != undefined) {
		currentUserProducts.forEach(product => {
			const matchingAudience = audience.find(a => a.id === product.id);
			product.audience = matchingAudience ? matchingAudience.audience : [];
		});
	}

	return currentUserProducts;
}

function resetProductsSiteInstalledInfo(currentUserProducts: UserProduct[]){
	currentUserProducts.forEach(product => {
		product.sitesWithProductInstalled = undefined;
		product.isOutdatedInSiteCollection = undefined;
	})

	return currentUserProducts;
}
